import { computed, ComputedRef, reactive, ref } from "vue";
import axios from "axios";
import type { TypedCursorPage } from "../laravel.d.ts";
import { logError, logWarning } from "../helpers/logger";

type changePageOptions = {
    clear: boolean,
}

export interface ReactivePaginator<T> {
    /**
     * The paginated data
     */
    page: TypedCursorPage<T>,

    /**
     * If true then we are currently loading data
     */
    loading: ComputedRef<boolean>,

    /**
     * true if there is another page to traverse.
     */
    hasNext: ComputedRef<boolean>,

    /**
     * True if there is a previous page to traverse
     */
    hasPrev: ComputedRef<boolean>,

    /**
     * Go to the next page of data (if exists)
     */
    next: (options: changePageOptions) => Promise<void>,

    /**
     * Go to the prev page of data (if exists)
     */
    prev: (options: changePageOptions) => Promise<void>,

    /**
     * Replace the paginated data with a new page
     */
    replacePage(data: TypedCursorPage): void,

    /**
     * Clear all data from this reactive paginator
     */
    clearAllData: () => void;

    /**
     * Append a single item to the end of the data array
     */
    append: (item: T) => void;

    /**
     * Prepend a single item to the beginning of the data array
     */
    prepend: (item: T) => void;

    /**
     * Allows you to mutate/format the data returned by the server
     * before saving to the paginator
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the raw server row type is owned by the caller's formatter; unknown would reject typed formatters under contravariance
    setFormatter: (newFormatter: (item: any) => T) => ReactivePaginator<T>;

    /**
     * Allows to overwrite default error handler.
     */
    setErrorHandler: (handler: (theUrl: string, e: unknown) => void) => ReactivePaginator<T>;
}

/**
 * ReactivePaginator is a data structure that turns laravel pagination responses into a reactive
 * data structure that will automatically update your data. It contains all of the data sent
 * from the server and also contains some helper methods, next() and prev() that will
 * automatically load the next page of data from the server using the next_page_url
 * that is generated by the pagination response
 */
export default function ReactivePaginator<T = unknown>(
    {
        data = [],
        per_page = 0,
        next_cursor = null,
        next_page_url = null,
        path = null,
        prev_cursor = null,
        prev_page_url = null,
    }: Partial<TypedCursorPage<T>>,
    http: HttpInterface = axios
): ReactivePaginator<T> {
    const page = reactive<TypedCursorPage<T>>({
        data,
        per_page,
        next_cursor,
        next_page_url,
        path,
        prev_cursor,
        prev_page_url,
    });

    const loading = ref(false);
    const hasNext = computed(() => page.next_cursor !== null);
    const hasPrev = computed(() => page.prev_cursor !== null);

    /**
     * This function is used to process the data pulled from the server before saving to the paginator.
     * It allows us to wrap the data sent from the server in front end model objects. By default
     * the function just passes back the data unmodified, but if you want to change that just
     * pass a function to the setFormatter method. For an example of doing this check out
     * UrlsApi in the teams project
     */
    let dataFormatter = (item: unknown): T => item as T;
    let onError = (theUrl: string, e: unknown) => logError("[ReactivePaginator]: Could not fetch prev/next page.");

    const replacePage = function (newData: TypedCursorPage) {
        page.per_page = newData.per_page;
        page.data = [...page.data, ...Object.values(newData.data).map((item) => dataFormatter(item))] as typeof page.data;
        page.path = newData.path;
        page.prev_page_url = newData.prev_page_url;
        page.prev_cursor = newData.prev_cursor;
        page.next_page_url = newData.next_page_url;
        page.next_cursor = newData.next_cursor;
    }

    const clearAllData = function () {
        page.data = [];
        page.per_page = 0;
        page.next_cursor = null;
        page.next_page_url = null;
        page.path = null;
        page.prev_cursor = null;
        page.prev_page_url = null;
    }

    const append = function (item: T) {
        page.data = [...page.data, item] as typeof page.data;
    }

    const prepend = function (item: T) {
        page.data = [item, ...page.data] as typeof page.data;
    }

    /**
     * Fetch the previous page of data. The callbacks are used by the vue infinite loader component
     */
    const prev = async function ({ clear = true }: changePageOptions) {
      if (!hasPrev.value) {
          logWarning("no next page to fetch");
          return;
      }

      loading.value = true;

      try {
        const response = await http.get<TypedCursorPage>(page.prev_page_url as string);

        if (clear) {
            page.data = [];
        }

        replacePage(response.data);
      } catch(e) {
        onError(page.prev_page_url || "previous page", e);
      }finally {
        loading.value = false;
      }
    }

    /**
     * Fetch the next page of data. The callbacks are used by the vue infinite loader component
     */
    const next = async function ({ clear = true }: changePageOptions) {
      if (!hasNext.value) {
          logWarning("no next page to fetch");
          return;
      }

      loading.value = true;

      try {
        const response = await http.get<TypedCursorPage>(page.next_page_url as string);

        if (clear) {
            page.data = [];
        }

        replacePage(response.data);
      } catch(e) {
        onError(page.next_page_url || "next page", e);
      } finally {
          loading.value = false;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- the raw server row type is owned by the caller's formatter; unknown would reject typed formatters under contravariance
    const setFormatter = (newFormatter: (item: any) => T): ReactivePaginator<T> => {
        dataFormatter = newFormatter;
        return paginator;
    };

    const setErrorHandler = (handler: (theUrl: string, e: unknown) => void) => {
      onError = handler;
      return paginator;
    };

    const paginator: ReactivePaginator<T> = {
        page: page as TypedCursorPage<T>,
        loading: computed(() => loading.value),
        hasNext,
        hasPrev,
        next,
        prev,
        replacePage,
        clearAllData,
        append,
        prepend,
        setFormatter,
        setErrorHandler,
    };

    return paginator;
}
